import { useState, useEffect, useRef } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import Input from '../../../../components/Input';
import type { FormValue } from '../searchSchema';
import useFetchSummaryCompanies from '../../api/useFetchSummaryCompanies';

interface CompanySelectBaseProps {
  value?: number[];
  onChange?: (companyIds: number[]) => void;
  onSelectName?: (name: string) => void;
  placeholder?: string;
  includeSharedJobPost?: boolean;
}

// ベースコンポーネント（react-hook-formに依存しない）
export function CompanySelectBase({
  value = [],
  onChange,
  onSelectName,
  placeholder = '企業名を入力して検索',
  includeSharedJobPost = false,
}: CompanySelectBaseProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useFetchSummaryCompanies(
    debouncedQuery,
    includeSharedJobPost
  );
  const dataRef = useRef(data);
  dataRef.current = data;

  // debounce検索クエリ
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedQuery(searchValue);
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchValue]);

  // 選択された企業が1社の場合、その名前をインプットに表示
  useEffect(() => {
    if (value.length === 1 && dataRef.current?.pages) {
      const allRecords = dataRef.current.pages.flatMap((page) => page.records);
      const company = allRecords.find((c) => c.id === value[0]);
      if (company) {
        setSearchValue(company.name);
      }
    } else if (value.length === 0) {
      setSearchValue('');
    }
  }, [value]);

  const companies = data?.pages?.flatMap((page) => page.records) ?? [];

  const handleScroll = () => {
    const element = listRef.current;
    if (!element) return;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleCompanySelect = (companyId: number, companyName: string) => {
    onChange?.([companyId]);
    onSelectName?.(companyName);
    setSearchValue(companyName);
    setIsOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    setSearchValue(inputValue);
    // 入力値が空になったら選択をクリア
    if (inputValue === '') {
      onChange?.([]);
    }
  };

  return (
    <div className="relative w-full">
      <Input
        placeholder={placeholder}
        value={searchValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        className="w-full"
      />

      {isOpen && (
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="absolute top-full left-0 z-10 mt-1 max-h-[280px] w-full animate-in overflow-y-auto rounded-sm bg-white px-xs shadow-layer3 duration-200 fade-in-0 slide-in-from-top-2"
        >
          {companies.length > 0 ? (
            companies.map((company) => (
              <div
                key={company.id}
                className="flex h-xxl cursor-pointer items-center gap-2 px-2 hover:bg-gray"
                onMouseDown={() => handleCompanySelect(company.id, company.name)}
              >
                <span className="text-sm">{company.name}</span>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-sm text-gray-500">
              該当する企業が見つかりません
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// react-hook-form用のラッパー（後方互換性のため）
interface CompanySelectProps {
  onSetCompanyIds?: (companyIds: number[]) => void;
  placeholder?: string;
  includeSharedJobPost?: boolean;
}

export default function CompanySelect({
  onSetCompanyIds,
  placeholder,
  includeSharedJobPost,
}: CompanySelectProps) {
  const { control, watch } = useFormContext<FormValue>();
  const companyIds = watch('companyIds');
  const { field } = useController({ name: 'companyIds', control });

  const handleChange = (newCompanyIds: number[]) => {
    field.onChange(newCompanyIds);
    onSetCompanyIds?.(newCompanyIds);
  };

  return (
    <CompanySelectBase
      value={companyIds}
      onChange={handleChange}
      placeholder={placeholder}
      includeSharedJobPost={includeSharedJobPost}
    />
  );
}
